/**
 * Real, OAuth-backed statutory-payment + filing-calendar resolvers for the
 * run-payroll wizard's post-run step (C9, #22331) — the REAL counterpart to
 * the statutory/filing two-thirds of `dev_pay_run_post_actions.ts` (now
 * DELETED — this module, plus the payslip/disbursement/bank-picker third C8
 * (#22330) shipped in `oauth_pay_run_post_actions.ts`, together fully
 * supersede it), reachable in every environment (including production), not
 * just local dev — same "real production OAuth-gated work" stance as
 * `oauth_pay_run_post_actions.ts` / `use_my_pay.ts` / `use_session_facts.ts`.
 *
 * Uses the real bearer client (`getTrpcClient()`, `lib/trpc.ts`) — never
 * `credentialedFetch`/`dev_auth_bypass.ts`'s cookie-session transport, and
 * never `resolveDevEntityContext`'s `firm.list`/`entity.list` round trip.
 * `firm_id`/`entity_id` are supplied BY THE CALLER (resolved once via
 * `useOAuthEntityContext()` in `use_pay_run_post_actions.ts`), exactly like
 * `oauth_pay_run_post_actions.ts`'s own functions.
 *
 * ── The bank-account picker is NOT `account.list` ─────────────────────────
 *
 * `dev_pay_run_post_actions.ts`'s `list_bank_accounts` was `account.list`-
 * backed, cookie-session-only. `account.list` is deliberately NOT
 * OAuth-allowlisted for a payroll token (C5/#22324's own design decision —
 * see `oauth_pay_run_post_actions.ts`'s `fetchPaymentSourceAccounts` doc
 * comment), so the OAuth-backed statutory-payment picker reuses that SAME
 * narrow `payroll.listPaymentSourceAccounts` procedure the disbursement
 * dialog already uses (both pickers want the identical "active asset
 * bank/cash accounts" set — `PAYMENT_SOURCE_ASSET_SUB_TYPES` in
 * `apps/api/src/services/payment_account_guard.ts` is a strict subset of
 * `assertPayrollBankAccount`'s broader write-side guard, so every account
 * this picker offers is always accepted by `recordStatutoryPayment`). This
 * module does not re-fetch it — `usePayRunPostActions()` maps
 * `fetchPaymentSourceAccounts`'s result down to `BankAccountOption` (drops
 * `currency_code`) rather than this file duplicating that network call.
 *
 * ── The entitlement gates (verified against apps/api/src/trpc/routers/
 *    payroll.ts and filing_calendar.ts directly, not assumed) ─────────────
 *
 * - `listStatutoryPayments` is `payrollEntityProcedure` (query-only);
 *   `recordStatutoryPayment` / `voidStatutoryPayment` are
 *   `payrollWriteProcedure`. All three are gated by the SAME single
 *   `payroll` add-on entitlement (`requirePayrollAddon`) as every other
 *   payroll procedure — classified via `isPayrollAddonRequiredError`
 *   (`lib/trpc.ts`), never a bare FORBIDDEN check (see that function's
 *   docblock for why).
 * - `filingCalendar.list` / `.generateUpcoming` / `.markFiled` are
 *   `entityProcedure` / `entityWriteProcedure` gated by the `reports_access`
 *   RBAC permission (`requireEntityPermission`), NOT the `payroll` add-on —
 *   a structurally different gate. A FORBIDDEN from any of these three is
 *   therefore NEVER classified as `entitlement_required` (that would be
 *   actively misleading — "buy the payroll add-on" copy for an unrelated
 *   RBAC/jurisdiction gate) — it always degrades to a generic error message,
 *   same as the deleted dev module's own stance. These three are also
 *   LK-jurisdiction-gated (`assertLkJurisdiction`, `NOT_FOUND` for non-LK
 *   entities).
 *
 * ── Dependency on C6 (#22325), NOT YET MERGED at the time this module was
 *    written ─────────────────────────────────────────────────────────────
 *
 * `filingCalendar.list` / `.generateUpcoming` / `.markFiled` were NOT
 * OAuth-allowlisted at all as of this commit (`apps/api/src/trpc/
 * oauth_allowlist.ts` only allowlisted `filingCalendar.calendar`) — C6
 * (#22325, PR #22349) adds the missing `requireOAuthScope` guards +
 * allowlist entries for these three. This module's calls are written
 * against C6's FINAL shape (`payroll:employer:read|write` for `list`,
 * `payroll:employer:write` for `generateUpcoming`/`markFiled`) so no further
 * change is needed here once C6 merges — but until it does, these three
 * calls will 403 in any real (non-dev-bypass) environment. See this PR's
 * body for the explicit dependency disclosure.
 *
 * ── Never throws ──────────────────────────────────────────────────────────
 *
 * Every function here resolves a discriminated `{ ok: false; ... }` result
 * on failure and logs one actionable message — never a mock fallback, never
 * a fabricated success (mirrors `oauth_pay_run_post_actions.ts`'s "no mock
 * fallback, real data or an explicit error state" stance exactly). Money
 * mutations (`recordStatutoryPayment`/`voidStatutoryPayment`) are NEVER
 * optimistic: the caller only learns of success after this module's
 * `await` resolves the server's real response.
 *
 * ── `computed_due_cents` is read verbatim from the server, never derived ──
 *
 * `fetchStatutoryPayments` returns `listStatutoryPayments`'s
 * `computed_due_cents` map exactly as the server sent it — this module
 * contains NO arithmetic that recomputes a statutory liability from gross
 * pay or any other client-held figure. The server itself documents (see
 * `apps/api/src/trpc/routers/payroll.ts`'s `listStatutoryPayments` doc
 * comment) that this is deliberate: the EPF/ETF/PAYE breakdown is computed
 * exactly once, in `approvePayRun`, and persisted onto each pay-run line —
 * recomputing it anywhere else (client or a second server query) risks the
 * exact kind of parallel-formula drift that caused #19887.
 */
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isNotFoundTrpcError,
  isPayrollAddonRequiredError,
} from '../trpc.js';
import { isStepUpRequired } from './step_up.js';

// ── Shared wire types ────────────────────────────────────────────────────

/** The four SL statutory remittance types — matches `STATUTORY_PAYMENT_TYPES` in `apps/api/src/trpc/routers/payroll.ts`. */
export type RealStatutoryPaymentType =
  | 'epf_employee'
  | 'epf_employer'
  | 'etf'
  | 'paye';

/** A `statutory_payments` row, narrowed to what the UI needs. */
export interface RealStatutoryPaymentRow {
  id: string;
  payment_type: RealStatutoryPaymentType;
  amount_cents: number;
  paid_on: string;
  reference: string | null;
}

/** A raw `filing_obligations` row from `filingCalendar.list`. */
export interface RealFilingObligationRow {
  obligation_id: string;
  report_id: string;
  due_date: string;
  status: string;
}

/**
 * How an OAuth-backed filing-calendar WRITE call (`generateUpcomingFilings`/
 * `markFiled` — the two whose failure reason actually reaches user-facing
 * copy, see `post_run_sections.tsx`) can fail, once classified:
 *
 * - `permission_required`: the `reports_access` RBAC permission is missing
 *   (`requireEntityPermission`), OR (pre-C6, #22325) the OAuth token isn't
 *   allowlisted for this procedure at all, OR the caller is a deny-by-default
 *   agent/API token. All three surface as a tRPC FORBIDDEN — deliberately
 *   collapsed into ONE user-facing reason rather than three, since none of
 *   the three distinctions is actionable for the end user (in every case,
 *   the fix is "ask an admin", not something the error copy can differentiate
 *   usefully).
 * - `jurisdiction_unsupported`: `assertLkJurisdiction` rejected the entity's
 *   country with `NOT_FOUND` (never leaks which countries ARE supported —
 *   security-rules.md).
 * - `error`: anything else (network failure, unexpected server error). The
 *   raw server/network message is logged to the console for debugging but
 *   NEVER surfaced as UI copy here — unlike `recordStatutoryPayment`'s
 *   amount-mismatch message (a deliberately-shown validation string), there
 *   is no filing-calendar failure message that is safe/meaningful to show a
 *   non-technical user verbatim (adversarial-review finding: an earlier
 *   version of this module surfaced the raw tRPC error message, which for a
 *   FORBIDDEN before C6 merges would have been the deny-by-default gate's
 *   own English sentence, untranslated, to si/ta/id users).
 */
export type FilingCalendarFailureReason =
  | 'permission_required'
  | 'jurisdiction_unsupported'
  | 'error';

/** Classifies a filing-calendar-router failure into a {@link FilingCalendarFailureReason}. Never throws (the caller's `catch` already has). */
function classifyFilingCalendarError(
  err: unknown,
): FilingCalendarFailureReason {
  if (isForbiddenTrpcError(err)) return 'permission_required';
  if (isNotFoundTrpcError(err)) return 'jurisdiction_unsupported';
  return 'error';
}

// ── Deps ─────────────────────────────────────────────────────────────────

export interface OAuthStatutoryFilingDeps {
  listStatutoryPayments: (input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
  }) => Promise<{
    computed_due_cents: Record<RealStatutoryPaymentType, number>;
    payments: RealStatutoryPaymentRow[];
  }>;
  recordStatutoryPayment: (input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    payment_type: RealStatutoryPaymentType;
    amount_cents: number;
    paid_on: string;
    reference?: string;
    bank_account_id: string;
  }) => Promise<{ payment_id: string }>;
  voidStatutoryPayment: (input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    payment_id: string;
  }) => Promise<{ ok: boolean }>;
  generateUpcomingFilings: (input: {
    firm_id: string;
    entity_id: string;
  }) => Promise<{ inserted: number; skipped: number; total_seeds: number }>;
  listFilingObligations: (input: {
    firm_id: string;
    entity_id: string;
  }) => Promise<{ obligations: RealFilingObligationRow[] }>;
  markFiled: (input: {
    firm_id: string;
    entity_id: string;
    obligation: string;
    period_label: string;
  }) => Promise<{ report_id: string; action: 'updated' | 'inserted' }>;
  markSubmitted: (input: {
    firm_id: string;
    entity_id: string;
    obligation_id: string;
  }) => Promise<{ obligation: RealFilingObligationRow }>;
}

/** The four SL statutory remittance types, for validating the wire's untyped `payment_type` TEXT column below. */
const REAL_STATUTORY_PAYMENT_TYPES: readonly RealStatutoryPaymentType[] = [
  'epf_employee',
  'epf_employer',
  'etf',
  'paye',
];

/**
 * `statutory_payments.payment_type` is a free-text `TEXT` column server-side
 * (validated at write time by `recordStatutoryPayment`'s zod schema, but not
 * narrowed on read). Returns `null` for an unrecognised value rather than
 * coercing it to some arbitrary member of {@link RealStatutoryPaymentType} —
 * an earlier version of this defaulted to `'paye'`, which would make an
 * UNRELATED, still-outstanding PAYE liability render as "Recorded" (with a
 * Void button) the moment ANY row had a corrupted `payment_type`, silently
 * hiding a real unremitted statutory liability (adversarial-review finding:
 * a money-visibility bug, not a cosmetic one, once this runs against
 * production data rather than only a local dev bypass). The caller
 * (`defaultDeps()` below) drops `null` rows entirely rather than surfacing
 * them, with a warning logged — this should never actually happen, since
 * every row is written through the zod-validated `recordStatutoryPayment`
 * mutation.
 */
export function narrowStatutoryPaymentType(
  value: string,
): RealStatutoryPaymentType | null {
  if (
    REAL_STATUTORY_PAYMENT_TYPES.includes(value as RealStatutoryPaymentType)
  ) {
    return value as RealStatutoryPaymentType;
  }
  console.warn(
    `[wagepro-web] statutory payment_type "${value}" is not a recognised RealStatutoryPaymentType -- dropping this row rather than mislabeling it`,
  );
  return null;
}

function defaultDeps(): OAuthStatutoryFilingDeps {
  const client = getTrpcClient();
  return {
    listStatutoryPayments: async (input) => {
      const result = await client.payroll.listStatutoryPayments.query(input);
      const payments: RealStatutoryPaymentRow[] = [];
      for (const payment of result.payments) {
        const payment_type = narrowStatutoryPaymentType(payment.payment_type);
        if (payment_type === null) continue;
        payments.push({
          id: payment.id,
          payment_type,
          amount_cents: payment.amount_cents,
          paid_on: payment.paid_on,
          reference: payment.reference,
        });
      }
      return { computed_due_cents: result.computed_due_cents, payments };
    },
    recordStatutoryPayment: (input) =>
      client.payroll.recordStatutoryPayment.mutate(input),
    voidStatutoryPayment: (input) =>
      client.payroll.voidStatutoryPayment.mutate(input),
    generateUpcomingFilings: (input) =>
      client.filingCalendar.generateUpcoming.mutate(input),
    listFilingObligations: (input) => client.filingCalendar.list.query(input),
    markFiled: (input) => client.filingCalendar.markFiled.mutate(input),
    markSubmitted: (input) => client.filingCalendar.markSubmitted.mutate(input),
  };
}

// ── Statutory payments ────────────────────────────────────────────────────

export type FetchStatutoryPaymentsResult =
  | {
      ok: true;
      computed_due_cents: Record<RealStatutoryPaymentType, number>;
      payments: RealStatutoryPaymentRow[];
    }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'error'; message: string };

/** Reads computed-due amounts + already-recorded payments for a pay run. Never throws. */
export async function fetchStatutoryPayments(
  input: { firm_id: string; entity_id: string; pay_run_id: string },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<FetchStatutoryPaymentsResult> {
  try {
    const result = await deps.listStatutoryPayments(input);
    return { ok: true, ...result };
  } catch (err) {
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] list statutory payments: entity does not have an active payroll add-on entitlement.',
      );
      return { ok: false, reason: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] list statutory payments: listStatutoryPayments failed',
      err,
    );
    return {
      ok: false,
      reason: 'error',
      message:
        err instanceof Error
          ? err.message
          : 'unknown error listing statutory payments',
    };
  }
}

export type RecordStatutoryPaymentResult =
  | { ok: true; payment_id: string }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'step_up_required' }
  | { ok: false; reason: 'error'; message: string };

/**
 * Records a statutory remittance payment. `amount_cents` MUST be the exact
 * server-computed due amount from `fetchStatutoryPayments` — the server
 * rejects any other figure with a clear `BAD_REQUEST`, surfaced verbatim via
 * `message` (a deliberately-shown validation string, same stance as
 * `oauth_pay_run_post_actions.ts`'s disbursement-file BAD_REQUEST message) —
 * `reason: 'entitlement_required'` is a SEPARATE, narrow branch so the UI
 * never renders that internal token as if it were message text (adversarial
 * -review finding: an earlier version returned a bare `{ reason: string }`
 * that let `'entitlement_required'` itself leak to the user as alert copy).
 * Never optimistic: the caller only learns of success once this function's
 * own `await` resolves the server's real response — there is no local state
 * flip anywhere in this module. Never throws.
 *
 * `recordStatutoryPayment` is a real `payrollWriteProcedure`, so it is ALSO
 * gated by `assertMoneyWriteAllowed` — a session that was never established
 * with two-factor authentication (AAL1) is rejected with `UNAUTHORIZED` +
 * `data.ledgerCode === 'LEDGER_AUTH_MONEY_WRITE_STEP_UP_REQUIRED'` (#22829,
 * same wire shape as `approvePayRun`'s #22821 rejection — see
 * `isStepUpRequired`'s own docblock, `lib/auth/step_up.ts`). That rejection
 * resolves the distinct `'step_up_required'` reason, checked BEFORE
 * `isPayrollAddonRequiredError` (both are FORBIDDEN/UNAUTHORIZED-shaped tRPC
 * errors matched by duck-typed shape, not by error class — same ordering
 * `data/use_pay_runs.ts`'s `approvePayRun` already uses) — never the generic
 * `'error'` state, since a blind retry can never clear an AAL2 step-up
 * requirement (an AAL1 token never self-upgrades; only a fresh sign-in round
 * trip, `restartSignIn()`, does).
 */
export async function recordStatutoryPayment(
  input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    payment_type: RealStatutoryPaymentType;
    amount_cents: number;
    paid_on: string;
    reference?: string;
    bank_account_id: string;
  },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<RecordStatutoryPaymentResult> {
  try {
    const { payment_id } = await deps.recordStatutoryPayment(input);
    return { ok: true, payment_id };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error(
        '[wagepro-web] record statutory payment: AAL2 step-up required -- rendering the 2FA guidance state.',
      );
      return { ok: false, reason: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] record statutory payment: entity does not have an active payroll add-on entitlement.',
      );
      return { ok: false, reason: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] record statutory payment: recordStatutoryPayment failed',
      err,
    );
    return {
      ok: false,
      reason: 'error',
      message:
        err instanceof Error
          ? err.message
          : 'unknown error recording statutory payment',
    };
  }
}

export type VoidStatutoryPaymentResult =
  | { ok: true }
  | { ok: false; reason: 'entitlement_required' }
  | { ok: false; reason: 'step_up_required' }
  | { ok: false; reason: 'error'; message: string };

/**
 * Voids a previously-recorded statutory payment, reversing its GL posting.
 * Never optimistic — same "server confirms first" contract as
 * {@link recordStatutoryPayment}. Checks the dep's own `ok` field (not just
 * "did it throw") before reporting success — a `{ ok: false }` response
 * (never actually produced by the real server today, but part of the dep's
 * declared type) must not be reported as a fabricated success (adversarial
 * -review finding). Never throws.
 *
 * `voidStatutoryPayment` is ALSO a real `payrollWriteProcedure`, gated by the
 * same `assertMoneyWriteAllowed` AAL2 step-up check as
 * {@link recordStatutoryPayment} (#22829) — see that function's doc comment
 * for the full rationale. Checked BEFORE `isPayrollAddonRequiredError`, same
 * ordering.
 */
export async function voidStatutoryPayment(
  input: {
    firm_id: string;
    entity_id: string;
    pay_run_id: string;
    payment_id: string;
  },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<VoidStatutoryPaymentResult> {
  try {
    const result = await deps.voidStatutoryPayment(input);
    if (!result.ok) {
      console.error(
        '[wagepro-web] void statutory payment: voidStatutoryPayment resolved ok:false',
      );
      return {
        ok: false,
        reason: 'error',
        message: 'unable to void this statutory payment',
      };
    }
    return { ok: true };
  } catch (err) {
    if (isStepUpRequired(err)) {
      console.error(
        '[wagepro-web] void statutory payment: AAL2 step-up required -- rendering the 2FA guidance state.',
      );
      return { ok: false, reason: 'step_up_required' };
    }
    if (isPayrollAddonRequiredError(err)) {
      console.error(
        '[wagepro-web] void statutory payment: entity does not have an active payroll add-on entitlement.',
      );
      return { ok: false, reason: 'entitlement_required' };
    }
    console.error(
      '[wagepro-web] void statutory payment: voidStatutoryPayment failed',
      err,
    );
    return {
      ok: false,
      reason: 'error',
      message:
        err instanceof Error
          ? err.message
          : 'unknown error voiding statutory payment',
    };
  }
}

// ── Filing calendar (entity-wide, not scoped to one pay run) ─────────────

export type GenerateUpcomingFilingsResult =
  | { ok: true; inserted: number; skipped: number }
  | { ok: false; reason: FilingCalendarFailureReason };

/** Seeds the next 12 months of statutory filing obligations. Idempotent (skips existing rows). Never throws. */
export async function generateUpcomingFilings(
  input: { firm_id: string; entity_id: string },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<GenerateUpcomingFilingsResult> {
  try {
    const { inserted, skipped } = await deps.generateUpcomingFilings(input);
    return { ok: true, inserted, skipped };
  } catch (err) {
    console.error(
      '[wagepro-web] generate upcoming filings: this entity may be missing the ' +
        '"reports_access" permission, may not be on a supported (LK) jurisdiction, ' +
        'or the OAuth token may lack payroll:employer:write.',
      err,
    );
    return { ok: false, reason: classifyFilingCalendarError(err) };
  }
}

export type ListFilingObligationsResult =
  | { ok: true; obligations: RealFilingObligationRow[] }
  | { ok: false; reason: FilingCalendarFailureReason };

/** Lists the entity's raw filing-obligation rows, most-due-first. Never throws. */
export async function listFilingObligations(
  input: { firm_id: string; entity_id: string },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<ListFilingObligationsResult> {
  try {
    const { obligations } = await deps.listFilingObligations(input);
    return { ok: true, obligations };
  } catch (err) {
    console.error(
      '[wagepro-web] list filing obligations: filingCalendar.list failed',
      err,
    );
    return { ok: false, reason: classifyFilingCalendarError(err) };
  }
}

export type MarkFiledResult =
  | { ok: true }
  | { ok: false; reason: FilingCalendarFailureReason };

/** Marks one filing obligation (by canonical `obligation:period_label` key) as filed. Never throws. */
export async function markFiled(
  input: {
    firm_id: string;
    entity_id: string;
    obligation: string;
    period_label: string;
  },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<MarkFiledResult> {
  try {
    await deps.markFiled(input);
    return { ok: true };
  } catch (err) {
    console.error(
      '[wagepro-web] mark filed: filingCalendar.markFiled failed',
      err,
    );
    return { ok: false, reason: classifyFilingCalendarError(err) };
  }
}

export type MarkSubmittedResult =
  | { ok: true }
  | { ok: false; reason: FilingCalendarFailureReason };

/**
 * Legacy sibling of {@link markFiled} — flips one filing obligation to
 * `submitted` by its opaque `obligation_id` ULID rather than the canonical
 * `obligation:period_label` key. Both procedures are OAuth-exposed under
 * `payroll:employer:write` (#23316 / #22325); the wagepro UI wires
 * `markSubmitted` next to `markFiled` in `FilingSection` for parity with the
 * server's still-supported legacy path.
 *
 * ── NOT_FOUND ambiguity ─────────────────────────────────────────────────────
 *
 * The router throws `NOT_FOUND` from TWO distinct causes here:
 *
 *   1. `assertLkJurisdiction` — the entity is not on the LK jurisdiction
 *      pack (pack existence is never leaked per security-rules.md).
 *   2. The obligation ULID is not found for this entity (a stale or typo
 *      `obligation_id`).
 *
 * `classifyFilingCalendarError` blindly maps every NOT_FOUND to
 * `jurisdiction_unsupported`, so case (2) currently surfaces "This
 * jurisdiction isn't supported" copy — misleading but not dangerous. `markFiled`
 * has no such branch (it upserts). Kept as-is here — introducing a new
 * discriminated `obligation_missing` reason would require expanding
 * {@link FilingCalendarFailureReason}, `FILING_FAILURE_KEY`, and translated
 * copy in all five locales, well outside the scope of #23316's single-
 * procedure wiring. Follow-up would flip the router's missing-obligation
 * throw to `BAD_REQUEST` so it falls into the generic `error` branch.
 *
 * Never throws.
 */
export async function markSubmitted(
  input: {
    firm_id: string;
    entity_id: string;
    obligation_id: string;
  },
  deps: OAuthStatutoryFilingDeps = defaultDeps(),
): Promise<MarkSubmittedResult> {
  try {
    await deps.markSubmitted(input);
    return { ok: true };
  } catch (err) {
    console.error(
      '[wagepro-web] mark submitted: filingCalendar.markSubmitted failed',
      err,
    );
    return { ok: false, reason: classifyFilingCalendarError(err) };
  }
}
